import React from 'react';
import PropTypes from 'prop-types';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { styled } from '@uhg-abyss/web/tools/styled';
import { PayerTableData } from '../../../common/PayerTableData';
import { PayerTableSearch } from '../../../common/PayerTableSearch';

const StyledAccordionHeader = styled(Accordion.Header, {
  fontWeight: 'bold',
  '&.abyss-accordion-header-subtext': {
    fontWeight: 'normal',
    paddingTop: '4px',
    fontSize: '16px',
  },
  '&.abyss-accordion-header-container': {
    padding: '12px 0px',
  },
});

const StyledAccordianContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

const StyledAccordianTrigger = styled(Accordion.Trigger, {
  border: 'none',
  boxShadow: 'none !important',
  '&:hover': {
    backgroundColor: 'white',
  },
});

const StyledAccordionItem = styled(Accordion.Item, {
  borderRadius: '8px !important',
  border: '1px solid $gray4 !important',
  marginBottom: '8px',
  '.abyss-accordion-content-text': {
    padding: '0px !important',
  },
});

const StyledTableRow = styled('tr', {
  borderTop: '1px solid $gray4',
  '&:hover': {
    backgroundColor: '$tint2',
  },
  cursor: 'pointer',
});

const StyledPayerIdTableData = styled('td', {
  fontSize: '16px',
  textAlign: 'right',
  paddingRight: '36px',
  verticalAlign: 'middle',
  color: 'black',
  variants: {
    isSelected: {
      selected: { fontWeight: 'bold' },
      notSelected: { fontWeight: 'normal' },
    },
  },
});

const StyledCheckMarkTableData = styled('td', {
  verticalAlign: 'middle',
  padding: '8px 15px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const StyledPayerNameTableData = styled('td', {
  verticalAlign: 'middle',
  fontSize: '16px',
  fontFamily: 'AbyssEnterpriseSansVF, Arial, sans-serif',
  color: 'black',
  padding: '8px 15px 8px 0px',
  variants: {
    isSelected: {
      selected: { fontWeight: 'bold' },
      notSelected: { fontWeight: 'normal' },
    },
  },
});

export const PayerGroup = ({
  payerNameSearchValue,
  setPayerNameSearchValue,
  payerIdSearchValue,
  setPayerIdSearchValue,
  filteredPayerData,
  selectedPayerRow,
  handlePayerRowClick,
  accordionState,
  handleAccordionClicked,
}) => {
  const renderRow = (data) => {
    return data.map((item) => {
      const [payerId, payerName] = item.label.split(' - ');
      return (
        <React.Fragment>
          <StyledTableRow
            key={item.label}
            onClick={() => handlePayerRowClick(item)}
            data-test-id='payer-select-table-row'
          >
            <StyledCheckMarkTableData>
              <Choose>
                <When condition={selectedPayerRow.payerLabel === item.label}>
                  <IconMaterial size='22px' icon='check' />
                </When>
              </Choose>
            </StyledCheckMarkTableData>
            <StyledPayerNameTableData
              isSelected={
                selectedPayerRow.payerLabel === item.label
                  ? 'selected'
                  : 'notSelected'
              }
            >
              {payerName}
            </StyledPayerNameTableData>

            <StyledPayerIdTableData
              isSelected={
                selectedPayerRow.payerLabel === item.label
                  ? 'selected'
                  : 'notSelected'
              }
            >
              {payerId}
            </StyledPayerIdTableData>
          </StyledTableRow>
        </React.Fragment>
      );
    });
  };

  return (
    <React.Fragment>
      <Accordion
        isCollapsible
        value={accordionState}
        onValueChange={handleAccordionClicked}
        defaultValue='payer-group'
        type='single'
      >
        <StyledAccordionItem value='payer-group'>
          <StyledAccordianTrigger>
            <StyledAccordionHeader subText={selectedPayerRow.payerLabel}>
              Payer Group
            </StyledAccordionHeader>
          </StyledAccordianTrigger>
          <Accordion.Content data-testid='payer-select'>
            <StyledAccordianContent>
              <PayerTableSearch
                payerNameSearchValue={payerNameSearchValue}
                setPayerNameSearchValue={setPayerNameSearchValue}
                payerIdSearchValue={payerIdSearchValue}
                setPayerIdSearchValue={setPayerIdSearchValue}
              />
              <PayerTableData
                colWidths={['10%', '78%', '12%']}
                data={filteredPayerData}
                renderRow={renderRow}
              />
            </StyledAccordianContent>
          </Accordion.Content>
        </StyledAccordionItem>
      </Accordion>
    </React.Fragment>
  );
};

PayerGroup.propTypes = {
  payerNameSearchValue: PropTypes.string.isRequired,
  setPayerNameSearchValue: PropTypes.func.isRequired,
  payerIdSearchValue: PropTypes.string.isRequired,
  setPayerIdSearchValue: PropTypes.func.isRequired,
  filteredPayerData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedPayerRow: PropTypes.shape({
    payerName: PropTypes.string.isRequired,
    payerId: PropTypes.string.isRequired,
    payerLabel: PropTypes.string.isRequired,
  }),
  handlePayerRowClick: PropTypes.func.isRequired,
  accordionState: PropTypes.string.isRequired,
  handleAccordionClicked: PropTypes.func.isRequired,
};
PayerGroup.defaultProps = {
  selectedPayerRow: null,
};
